
import { Button } from "@/components/ui/button";
import { Plus, Search, X } from "lucide-react";
import { useContacts } from "@/hooks/useContacts";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface ContactPerson {
  id: string;
  name: string;
  email: string;
  role?: string;
  phone?: string;
}

interface ContractContactsSectionProps {
  selectedContacts: string[];
  setSelectedContacts: React.Dispatch<React.SetStateAction<string[]>>;
  setContactModalOpen: (open: boolean) => void;
  existingContacts?: ContactPerson[];
}

export function ContractContactsSection({
  selectedContacts,
  setSelectedContacts,
  setContactModalOpen,
  existingContacts = []
}: ContractContactsSectionProps) {
  const { useContactsQuery } = useContacts();
  const { data: contactPersons = [] } = useContactsQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectorOpen, setSelectorOpen] = useState(false);
  
  // Get selected contact details, prioritizing existingContacts
  const selectedContactsDetails = useMemo(() => {
    // Create a Map for O(1) lookup with proper key-value pairs
    const contactMap = new Map<string, ContactPerson>();
    
    // Add existing contacts to the map
    existingContacts.forEach(contact => {
      contactMap.set(contact.id, contact);
    });
    
    // Add all contactPersons to the map (potentially overriding existingContacts if IDs match)
    contactPersons.forEach(contact => {
      contactMap.set(contact.id, contact);
    });
    
    return selectedContacts
      .map(id => contactMap.get(id))
      .filter((contact): contact is ContactPerson => contact !== undefined);
  }, [selectedContacts, contactPersons, existingContacts]);

  // Filter contacts based on search term
  const filteredContacts = contactPersons.filter(contact => 
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (contact.role && contact.role.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Toggle contact selection
  const toggleContactSelection = (contactId: string) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(prev => prev.filter(id => id !== contactId));
    } else {
      setSelectedContacts(prev => [...prev, contactId]);
    }
  };

  // Remove a contact
  const removeContact = (contactId: string) => {
    setSelectedContacts(prev => prev.filter(id => id !== contactId));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Contact Persons</h3>
          <p className="text-sm text-muted-foreground">
            Assign contacts to this contract
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSelectorOpen(true)}
          >
            <Search className="h-4 w-4 mr-2" /> Select Contacts
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setContactModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" /> Add New Contact
          </Button>
        </div>
      </div>

      {/* Contact Selector Dialog */}
      <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Contacts</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="mb-4">
              <Input
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
                autoComplete="off"
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {filteredContacts.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No contacts found
                </p>
              ) : (
                filteredContacts.map(contact => (
                  <div 
                    key={contact.id}
                    className={`p-3 border rounded-md cursor-pointer flex items-center justify-between ${
                      selectedContacts.includes(contact.id) ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                    }`}
                    onClick={() => toggleContactSelection(contact.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {contact.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-xs text-muted-foreground">{contact.email}</p>
                      </div>
                    </div>
                    {selectedContacts.includes(contact.id) && (
                      <Badge variant="outline" className="bg-primary text-primary-foreground">Selected</Badge>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setSelectorOpen(false)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Display selected contacts */}
      {selectedContactsDetails.length === 0 ? (
        <div className="text-center p-4 border rounded-md bg-muted/50">
          <p className="text-muted-foreground">No contacts selected.</p>
          <Button
            type="button"
            variant="link"
            onClick={() => setSelectorOpen(true)}
          >
            Select contacts
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {selectedContactsDetails.map((contact) => (
            <Card key={contact.id} className="border border-primary/50 bg-card">
              <CardContent className="pt-4 flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {contact.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{contact.name}</p>
                    <div className="text-xs text-muted-foreground space-y-1 mt-1">
                      <p>{contact.role}</p>
                      <p>{contact.email}</p>
                      {contact.phone && <p>{contact.phone}</p>}
                    </div>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6" 
                  onClick={() => removeContact(contact.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
